import { useEffect, useState } from 'react';
import { NoFeedBackYet } from '../components/NoFeedbackYet';
import { fourWheelerSales, sales } from '../constants/constants';
import useGetAllMonthlyCounter from '../hooks/useGetAllMonthlyCounter';
import { useGetNPSData } from '../hooks/useGetNPSData';
import ConcernCategoryBarChart from './ConcernCategoryBarChart';
import { DatePicker } from './DatePicker';
import DSENPSBarChart from './DSENPSBarChart';
import { DSEWiseNPSTable } from './DSEWiseNPSTable';
import { EnquiryNps } from './EnquiryNps';
import FeedbackPieChart from './FeedbackPieChart';
import Footer from './Footer';
import { LanguagePieChart } from './LanguagePieChart';
import ModelNPSBarChart from './ModelNPSBarChart';
import MonthwiseFeedbackChart from './MonthwiseFeedbackChart';
import NPSScoreWidget from './NPSScoreWidget';
import ReferralTable from './ReferralTable';
import SubConcernCategoryBarChart from './SubConcernCategoryBarChart ';
import TLNPSBarChart from './TLNPSBarChart';
import { TLWiseNPSTable } from './TLWiseNPSTable';
import UserDataTable from './UserDataTable';

const FeedbackChart = () => {
  const {
    filterDataForDate,
    npsDetails,
    npsData,
    noFeedback,
    failed,
    user,
    loading,
    businessChartData,
    concernData,
    monthWiseConsumedMessages,
    npsScore,
    responseRatio,
    subConcernData,
    activeSlug,
    dateCount,
    referralData,
    mtdNpsScore,
    enquirySalesMap,
    salesData,
    enquiryData,
    walkInNpsScore,
  } = useGetNPSData();

  const { totalMonthlyCounter, totalMonthlyCounterLimit } =
    useGetAllMonthlyCounter();

  const [filterDate, setFilterDate] = useState({
    startDate: null,
    endDate: null,
  });

  useEffect(() => {
    filterDataForDate(filterDate);
  }, [filterDate]);

  if (loading) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-white dark:bg-gray-900">
        <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100">
      {npsDetails ? (
        <div className="p-6">
          {/* Top row: Date Filter + Monthly Counter */}
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4 mb-6">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
              <div>
                <p className="text-lg font-semibold">Filter by Date</p>
                <DatePicker
                  setFilterDate={setFilterDate}
                  filterDate={filterDate}
                />
              </div>
              <div>
                <p className="text-sm uppercase text-white-500">
                  Monthly Counter
                </p>
                <p className="text-3xl font-bold text-white-600">
                  {totalMonthlyCounter}/{totalMonthlyCounterLimit}
                </p>
              </div>
            </div>
          </div>

          {/* KPI Widgets */}
          <div
            className={`grid gap-6 md:grid-cols-2`}
          >
            {npsScore !== null && (
              <NPSScoreWidget
                score={npsScore}
                totalResponders={npsData.length}
                monthWiseConsumedMessages={monthWiseConsumedMessages}
                responseRatio={responseRatio}
                isMonth={false}
                type="overall"
              />
            )}
            {npsScore !== null && (
              <NPSScoreWidget
                score={mtdNpsScore.npsScore}
                totalResponders={mtdNpsScore.totalData}
                monthWiseConsumedMessages={monthWiseConsumedMessages}
                responseRatio={mtdNpsScore.responseRatio}
                isMonth={true}
                type="this month"
              />
            )}
            
          </div>

          {/* Pie charts */}
          <div className="grid md:grid-cols-2 gap-6 mt-6">
            {npsData.length > 0 && (
              <FeedbackPieChart data={businessChartData} npsOverall={true} />
            )}
            {npsData.length > 0 && (
              <FeedbackPieChart data={businessChartData} npsOverall={false} />
            )}
          </div>

          {/* Model Charts */}
          <div className="mt-6 space-y-6">
            {npsData.length > 0 && (
              <ModelNPSBarChart data={businessChartData} />
            )}
            {activeSlug !== 'service' && (
              <div className="grid md:grid-cols-2 gap-6">
                {npsData.length > 0 && (
                  <DSENPSBarChart data={businessChartData} />
                )}
                {npsData.length > 0 && user?.category === fourWheelerSales && (
                  <TLNPSBarChart data={businessChartData} />
                )}
              </div>
            )}

            {npsData.length > 0 && (
              <MonthwiseFeedbackChart data={businessChartData} />
            )}
            {npsData.length > 0 && (
              <ConcernCategoryBarChart concernData={concernData} />
            )}
            {npsData.length > 0 && (
              <SubConcernCategoryBarChart subConcernData={subConcernData} />
            )}
          </div>

          {/* User Data Table */}
          {npsData.length > 0 && (
            <div className="mt-6">
              <UserDataTable
                npsData={npsData}
                noFeedback={noFeedback}
                failed={failed}
                feedbackType={activeSlug}
                dateUploadCount={dateCount}
                salesData={salesData}
                enquiryData={enquiryData}
              />
            </div>
          )}

          {/* Enquiry NPS Table */}
          {npsData.length > 0 && activeSlug === sales.toLowerCase() && (
            <EnquiryNps
              notRetailNps={enquirySalesMap.notRetailed}
              retailNps={enquirySalesMap.retailed}
            />
          )}

          {/* Tables */}
          {
            activeSlug !== "service" &&
              <div className="mt-6 space-y-6">
                {npsData.length > 0 && <DSEWiseNPSTable data={businessChartData} />}
                {npsData.length > 0 && user?.category === fourWheelerSales && (
                  <TLWiseNPSTable data={businessChartData} />
                )}
              </div>
          }

          {/* Language / Referrals */}
          <div className="grid md:grid-cols-2 gap-6 mt-6">
            {npsData.length > 0 && (
              <LanguagePieChart data={businessChartData} />
            )}
            {npsData.length > 0 && activeSlug === sales.toLowerCase() && (
              <ReferralTable referralData={referralData} />
            )}
            {activeSlug.includes('enquiry') && npsScore !== null && (
              <NPSScoreWidget
                score={walkInNpsScore.npsScore}
                totalResponders={walkInNpsScore.totalData}
                monthWiseConsumedMessages={monthWiseConsumedMessages}
                responseRatio={walkInNpsScore.responseRatio}
                isMonth={false}
                type="walk-in"
              />
            )}
          </div>
        </div>
      ) : (
        <NoFeedBackYet />
      )}
      <Footer />
    </div>
  );
};

export default FeedbackChart;
